import React, { FC, useEffect, useState } from 'react'
import { Column } from 'react-table'
import Table from '../../components/Table'
import { Button, Container, FormControl, Grid, InputLabel, makeStyles, Select, TextField, Typography } from '@material-ui/core'
import { Autocomplete } from '@material-ui/lab'
import DateFnsUtils from '@date-io/date-fns'
import { MuiPickersUtilsProvider, KeyboardDatePicker } from '@material-ui/pickers'
import * as StatsAPI from '../../api/statistics/statistics'
import * as StatsModels from '../../api/statistics/models'
import * as ShortAPI from '../../api/short/index'
import * as ShortModels from '../../api/short/models'
import moment from "moment"
import "moment/locale/da"
import daLocale from "date-fns/locale/da"
import NoDataDialog from '../../components/dialogs/ErrorDialog'
import APIRequest from '../../components/helpers/APIRequest'

moment.locale("da")

const useStyles = makeStyles((theme) => ({
    paper: {
        [theme.breakpoints.up('sm')]: {
            padding: '0',
        },
        marginTop: theme.spacing(3),
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
    },
    formControl: {
        margin: theme.spacing(1),
        minWidth: 120,
    },
    buts: {
        width: '100%',
        height: '100%'
    },
    grid: {
        width: '100%', // Fix IE 11 issue.
        marginTop: theme.spacing(3),
    },
    textHelp: {
        marginBottom: '1rem'
    },
    margTop: {
        marginTop: theme.spacing(2),
    }
}))

const Statistics: FC = () => {
    const classes = useStyles()

    const [currentLocation, setCurrentLocation] = useState(-1)
    const [locations, setLocations] = useState<ShortModels.Location[]>([])
    const [isPendingLocations, setIsPendingLocations] = useState(false)
    const [runLocations, setRunLocations] = useState(true)

    const [devices, setDevices] = useState<ShortModels.Device[]>([])
    const [isPendingDevices, setIsPendingDevices] = useState(false)
    const [runGetDevices, setRunGetDevices] = useState(false)

    const [data, setData] = useState<StatsModels.PaymentResponse>({version: "", id: "", result: [[], [], [], []]})
    const [cardPaymentData, setCardPaymentData] = useState<StatsModels.CardPaymentData>(new StatsModels.CardPaymentData())
    const [emptyingData, setEmptyingData] = useState<StatsModels.EmptyingResponse>({version: "", id: "", result: []})
    const [postPaymentData, setPostPaymentData] = useState<StatsModels.PostPaymentResponse>({version: "", id: "", result: []})
    const [isPending, setIsPending] = useState(false)
    const [run, setRun] = useState(false)
    const [showError, setShowError] = useState(false)
    const [fromDate, setFromDate] = useState<Date | null>(new Date())
    const [toDate, setToDate] = useState<Date | null>(new Date())

    const [currentStat, setCurrentStat] = useState<number>(0)
    const [selectedDevices, setSelectedDevices] = useState<ShortModels.Device[]>([])

    useEffect(() => {
        if (locations.length > 0 && currentLocation === -1) {
            setCurrentLocation(locations[0].id)
        }
    }, [locations, currentLocation])

    useEffect(() => {
        if (currentLocation > 0) {
            setRunGetDevices(true)
        }
    }, [currentLocation])

    const columns: Column<StatsModels.PaymentData>[] = React.useMemo(() => [
        {Header: 'Automat', id:"0", accessor: "deviceName" }, 
        {Header: 'Beløb', id:"1", accessor: "amount"}, 
        {Header: 'Antal', id:"2", accessor: "count"}
    ], [])

    const dynamicColumns: Column<Array<string|number>>[] = React.useMemo(() => {
        var headers: Array<{Header: string, accessor: (e: Array<string|number>) => string, id: string}> = []
        var count = cardPaymentData.result.headers.length
        if (count < 1) {
            return [{Header: " ", accessor: e => ""}]
        }
        // Create a dynamic list of headers
        cardPaymentData.result.headers.forEach((element, index) => {
            headers.push({ Header: cardPaymentData.result.headers[index], accessor: e => "" + e[index], id: "" + index})
        });
        return headers
    }, [cardPaymentData])

    const columnsEmptyingReport: Column<StatsModels.EmptyingData>[] = React.useMemo(() => [
        {Header: "Automat", id: "0", accessor: "deviceName"}, 
        {Header: "Tømme nr", id: "1", accessor: "emptyNr"}, 
        {Header: "Tømme tid", id: "2", accessor: "emptyTime"}, 
        {Header: "Beløb", id: "3", accessor: "amount"},
        {Header: "Værdi 1 kr", id: "4", accessor: "valueDk001"},
        {Header: "Antal 1 kr", id: "5", accessor: "countDk001"}, 
        {Header: "Værdi 2 kr", id: "6", accessor: "valueDk002"}, 
        {Header: "Antal 2 kr", id: "7", accessor: "countDk002"},
        {Header: "Værdi 5 kr", id: "8", accessor: "valueDk005"}, 
        {Header: "Antal 5 kr", id: "9", accessor: "countDk005"}, 
        {Header: "Værdi 10 kr", id: "10", accessor: "valueDk010"}, 
        {Header: "Antal 10 kr", id: "11", accessor: "countDk010"},
        {Header: "Værdi 20 kr", id: "12", accessor: "valueDk020"}, 
        {Header: "Antal 20 kr", id: "13", accessor: "countDk020"}, 
        {Header: "Værdi tom", id: "14", accessor: "valueDkEmpty"}, 
        {Header: "Rabatkode", id: "15", accessor: "valueTotal"}
    ], [])

    const columnsPostPaymentReport: Column<StatsModels.PostPaymentData>[] = React.useMemo(() => [
        {Header: "Automat", id: "0", accessor: "deviceName"}, 
        {Header: "Ticket", id: "1", accessor: "ticket"}, 
        {Header: "Nummerplade", id: "2", accessor: "plate"}, 
        {Header: "Start", id: "3", accessor: "checkin"},
        {Header: "Slut", id: "4", accessor: "checkout"}, 
        {Header: "Status", id: "5", accessor: "status"}, 
        {Header: "Korttype", id: "6", accessor: "cardType"}, 
        {Header: "Kort", id: "7", accessor: "card"},
        {Header: "Beløb", id: "8", accessor: "amount"}, 
        {Header: "Rabatkode", id: "9", accessor: "discountCode"}, 
        {Header: "Rabattype", id: "10", accessor: "discountType"}
    ], [])

    const RequestData = () => {
        const fixDate = (date: Date | null, startOfDay: boolean) => {
            // We have to send the date with a UTC timezone, or the server will get the value wrong
            var d1 = date ? date : new Date()
            var d = new Date(Date.UTC(d1.getFullYear(), d1.getMonth(), d1.getDate(), 0, 0, 0, 0))
            
            return d
        }
        const param = (): StatsModels.Params => {
            // Choose all devices if none are chosen
            var meters = selectedDevices.length < 1 ? devices : selectedDevices;
            return {location: currentLocation, body: {Start: fixDate(fromDate, true), End: fixDate(toDate, false), Meters: meters.map((d) => d.id)}}
        }

        const stat = Number(currentStat)
        if (stat <= 4) {
            return (<APIRequest<StatsModels.PaymentResponse>
                run={run} setRun={setRun}
                pending={isPending} setIsPending={setIsPending}
                req={() => {
                    switch (stat) {
                        case 1: return StatsAPI.getStatsDebitCardPaymentByMeter(param())
                        case 2: return StatsAPI.getStatsCoinPaymentByMeter(param())
                        case 3: return StatsAPI.getStatsFeeByMeterAndCard(param())
                        case 4: return StatsAPI.getStatsMobilePayPaymentByMeter(param())
                        default /* also 0 */: return StatsAPI.getStatsCreditCardPaymentByMeter(param())
                    }
                }}
                setData={setData}
                setShowError={setShowError}
            />)
        } else if (stat <= 6) {
            return (<APIRequest<StatsModels.CardPaymentData>
                run={run} setRun={setRun}
                pending={isPending} setIsPending={setIsPending}
                req={() => {
                    switch (stat) {
                        case 5: return StatsAPI.getStatsCardPaymentAmount(param())
                        default: return StatsAPI.getStatsCardPaymentCount(param())
                    }
                }}
                setData={setCardPaymentData}
                setShowError={setShowError}
            />)
        } else if (stat === 7) {
            return (<APIRequest<StatsModels.EmptyingResponse>
                run={run} setRun={setRun}
                pending={isPending} setIsPending={setIsPending}
                req={() => StatsAPI.getEmptyingReport(param())}
                setData={setEmptyingData}
                setShowError={setShowError}
            />)
        } else /*if (stat === 10)*/ {
            return (<APIRequest<StatsModels.PostPaymentResponse>
                run={run} setRun={setRun}
                pending={isPending} setIsPending={setIsPending}
                req={() => StatsAPI.getPostPaymentReport(param())}
                setData={setPostPaymentData}
                setShowError={setShowError}
            />)
        }
    }

    const RequestLocationsData = () => {
        return (<APIRequest<ShortModels.Location[]>
            run={runLocations} setRun={setRunLocations}
            pending={isPendingLocations} setIsPending={setIsPendingLocations}
            req={ShortAPI.getLocations}
            setData={setLocations}
        />)
    }

    const RequestDevices = () => {
        return (
          <APIRequest<ShortModels.Device[]>
              run={runGetDevices} setRun={setRunGetDevices}
              pending={isPendingDevices} setIsPending={setIsPendingDevices}
              req={() => ShortAPI.getDevices(currentLocation)}
              setData={setDevices}
          />
        );
    }

    const handleStatsChange = (event: React.ChangeEvent<{ name?: string; value: unknown }>) => {
        const stat = event.target.value as number
        if (stat !== currentStat) {
            setCurrentStat(stat)

            // Clear all tables
            setData({version: "", id: "", result: [[], [], [], []]})
            setCardPaymentData(new StatsModels.CardPaymentData())
            setEmptyingData({version: "", id: "", result: []})
            setPostPaymentData({version: "", id: "", result: []})
        }
    }

    const setLocationSelection = (newSelection: ShortModels.Device[] | null) => {
        if (newSelection) {
            setSelectedDevices(newSelection)
        } else {
            setSelectedDevices([])
        }
    }

    return (
        <Container maxWidth={false}>
            <RequestData />
            <RequestLocationsData />
            <RequestDevices />
            <div className={classes.paper}>
                <Typography component="h1" variant="h5">Statistik</Typography>
                <Container maxWidth={"lg"} className={classes.margTop}>
                    {/* <div className={classes.paper}> */}
                    {/* <Typography component="h4" variant="h5">Der kan kun søges på enten </Typography> */}
                    <MuiPickersUtilsProvider locale={daLocale} utils={DateFnsUtils}>
                        <Grid container spacing={2}>
                            <Grid item xs={4}>
                                <FormControl className={classes.formControl}>
                                    <InputLabel htmlFor="current-stat-input">Statistik</InputLabel>
                                    <Select native value={currentStat} onChange={handleStatsChange} inputProps={{name: 'currentStat', id: 'current-stat-input' }}>
                                        <option value={0}>Kreditkort</option>
                                        <option value={1}>Debitkort</option>
                                        <option value={2}>Mønt</option>
                                        <option value={3}>Gebyr</option>
                                        <option value={4}>MobilePay</option>
                                        <option value={5}>Kortbetaling, beløb</option>
                                        <option value={6}>Kortbetaling, antal</option>
                                        <option value={7}>Tømnings rapport</option>
                                        <option value={8}>Transaktioner, alle</option>
                                        <option value={9}>Transaktioner, kun gebyr</option>
                                        <option value={10}>Post payment</option>
                                        <option value={11}>Alarmer</option>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={4}>
                                <KeyboardDatePicker disableToolbar variant="inline" format="dd-MM-yyyy" margin="normal" id="date-picker-inline" label="Fra" value={fromDate} onChange={setFromDate} KeyboardButtonProps={{'aria-label': 'Set dato'}} />
                            </Grid>
                            <Grid item xs={4}>
                                <KeyboardDatePicker disableToolbar variant="inline" format="dd-MM-yyyy" margin="normal" id="date-picker-inline" label="Til" value={toDate} onChange={setToDate} KeyboardButtonProps={{'aria-label': 'Set dato'}} />
                            </Grid>
                            <Grid item xs={6}>
                                <Autocomplete hidden={currentStat < 7} multiple value={selectedDevices} options={devices} getOptionLabel={option => option.name}
                                    renderInput={params => (<TextField {...params} label={selectedDevices.length === 0 ? "Automater - alle valgt" : "Automater"} variant="outlined" fullWidth />)}
                                    onChange={(event: any, newSelection: ShortModels.Device[] | null) => setLocationSelection(newSelection)} />
                            </Grid>
                            <Grid item xs={6}>
                                <Button variant="contained" color="primary" className={classes.buts} onClick={() => setRun(true)}>Hent statistik</Button>
                            </Grid>
                        </Grid>
                    </MuiPickersUtilsProvider>
                    {/* </Paper> */}
                {/* </div> */}
                </Container>
                <div className={classes.grid}>
                    <Grid container spacing={2}>
                        <Grid hidden={Number(currentStat) > 4} item xs={12}>
                            <Table<StatsModels.PaymentData> columns={columns} data={dataElements(data, 0)} initialState={{pageSize: 150}} clickEvent={() => {}} update={null} pending={false} text={""}/>
                        </Grid>
                        <Grid hidden={Number(currentStat) > 3 || Number(currentStat)===2} item xs={12}>
                            <Table<StatsModels.PaymentData> columns={columns} data={dataElements(data, 1)} initialState={{pageSize: 150}} clickEvent={() => {}} update={null} pending={false} text={""}/>
                        </Grid>
                        <Grid hidden={Number(currentStat) < 5 || Number(currentStat) > 6} item xs={12}>
                            <Table<Array<string|number>> columns={dynamicColumns} data={dynamicColumnsData(cardPaymentData)} initialState={{pageSize: 150}} clickEvent={() => {}} update={null} pending={false} text={""}/>
                        </Grid>
                        <Grid hidden={Number(currentStat) !== 7} item xs={12}>
                            <Table<StatsModels.EmptyingData> columns={columnsEmptyingReport} data={emptyingData.result} initialState={{pageSize: 150}} clickEvent={() => {}} update={null} pending={false} text={""}/>
                        </Grid>
                        <Grid hidden={Number(currentStat) !== 10} item xs={12}>
                            <Table<StatsModels.PostPaymentData> columns={columnsPostPaymentReport} data={postPaymentData.result} initialState={{pageSize: 150}} clickEvent={() => {}} update={null} pending={false} text={""}/>
                        </Grid>
                    </Grid>
                </div>
            </div>
            <NoDataDialog open={showError} onClose={() => { setShowError(false) }} title={"Intet svar"} text={"Noget gik galt, prøv venligst igen senere."}/>
        </Container>
    )
}

const dataElements = (data: StatsModels.PaymentResponse | undefined, element: number) => {
    if (data !== undefined) {
        if (data.result !== undefined) {
            if (element < data.result.length) {
                return data.result[element]
            }
        }
    }

    // We don't have any data for this
    return []
}

const dynamicColumnsData = (data: StatsModels.CardPaymentData): Array<string|number>[] => {
    if (data !== undefined) {
        if (data.result !== undefined) {
            if (data.result.rows !== undefined) {
                return data.result.rows
            }
        }
    }

    // We don't have any data for this
    return []
}

export default Statistics
